import * as React from "react";
import {LabelContext} from "../../core/label.context"
import {Box} from "../box/box.component"
import * as yup from "yup"
import {SubmitHandler, useForm} from "react-hook-form"
import {yupResolver} from "@hookform/resolvers/yup"
import {InputElement} from "../input/input.component"
import {Button} from "../button/button.component"
import ReactDOM from "react-dom";
import {BRX_BASE_URL, calls, PROJECT_KEY} from "../../core/APIUtils";
import {RESTContext} from "../../core/rest.context";
import {StyledReturn} from "../../pages/return.style";
import {createPrice, parsePrice} from "../../common/constants";

const getRefundedAmount = (order: any): number => {
    if (!order?.paymentInfo?.payments) return 0;

    return order.paymentInfo.payments.reduce((total: number, payment: any) => {
        const refundTransactions =
            payment.obj.transactions?.filter(
                (t: { type: string }) => t.type === "Refund"
            ) ?? [];

        const refundSum = refundTransactions.reduce(
            (sum: number, t: { amount: { centAmount: number } }) =>
                sum + t.amount.centAmount,
            0
        );

        return total + refundSum;
    }, 0);
};

export const Returns = (props: any) => {
    const [ordersList, setOrdersList] = React.useState<any[]>([]);
    const {t} = React.useContext(LabelContext)
    const {ajax$} = React.useContext(RESTContext)

    React.useEffect(() => {
        ajax$(calls.Project.getOrders())
            .subscribe((orders: any) => {
                setOrdersList(orders?.results ?? [])
            })
    }, [])

    const returnedOrders = ordersList.filter((o: any) => o.returnInfo && o.returnInfo.length > 0)

    //mappa contenente per ogni ordine l'ammontare di euro rimborsati all'utente
    const amountsRefundedMap: Record<string, number> = {};
    for (const order of returnedOrders) {
        amountsRefundedMap[order.id] = getRefundedAmount(order);
    }

    if (returnedOrders.length === 0) {
        return (
            <StyledReturn>
                <h2 className="mb-0 pb-0 font-weight-bold">{t("ecommerce.myreturns.section-title")}</h2>
                <div dangerouslySetInnerHTML={{ __html: t("ecommerce.myreturns.startReturn") }}></div>

                <div className="text-center mt-3">
                    {t("ecommerce.myreturns.empty")}
                </div>
            </StyledReturn>
        )
    }

    return (
        <StyledReturn>
            <h2 className="mb-0 pb-0 font-weight-bold">{t("ecommerce.myreturns.section-title")}</h2>
            <div dangerouslySetInnerHTML={{ __html: t("ecommerce.myreturns.startReturn") }}></div>
            {returnedOrders.map((order: any) => (
                <Box key={order.id} className="secondary returnPage">
                    <div className="orderRelated">
                        <div>
                            {t("ecommerce.myreturns.order-related")} <span
                            className="font-weight-bold">{order.orderNumber}</span>
                        </div>

                        <div>
                            {t("ecommerce.myreturns.refund-amount")} {parsePrice(createPrice(amountsRefundedMap[order.id], order.totalPrice.currencyCode))}
                        </div>
                    </div>

                    {/* LISTA PRODOTTI RESI */}
                    <div>
                        {order.returnInfo.map((ri: any, idx: number) => {
                            const firstItemDate = ri.items.length > 0
                                ? new Date(ri.items[0].createdAt).toLocaleDateString()
                                : null
                            return (
                                <div key={idx} className="return returnPageElement">
                                    {firstItemDate && (
                                        <div className="font-weight-bold mb-3">
                                            {t("ecommerce.myreturns.date-request-return")} {firstItemDate}
                                        </div>
                                    )}
                                    {ri.items.map((item: any, index: number) => {
                                        const li = order.lineItems.find((l: any) => l.id === item.lineItemId)
                                        const img = li?.variant?.images?.[0]?.url
                                        const paymentState = item.paymentState
                                        const isLast = index === ri.items.length - 1
                                        let returnStatus
                                        if (paymentState === "Initial") {
                                            returnStatus = t("ecommerce.myreturns.state-initial")
                                        }
                                        if (paymentState === "Refunded") {
                                            returnStatus = t("ecommerce.myreturns.state-refunded")
                                        }
                                        if (paymentState === "NotRefunded") {
                                            returnStatus = t("ecommerce.myreturns.state-not-refunded")
                                        }

                                        return (
                                            <div key={item.lineItemId}
                                                 className={`row align-items-center ${ri.items.length > 1 && !isLast ? "mb-3" : ""}`}>
                                                <div className="col-sm-12 col-lg-10 imgReturnContainer">
                                                    <div className="imgReturn">
                                                        {img && (
                                                            <img
                                                                src={img}
                                                                alt=""
                                                                title=""
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="return-info">
                                                        <div className="product-name">{li?.name?.[order.locale]}</div>
                                                        <div className="sku pb-3">
                                                            {li?.productKey}
                                                        </div>
                                                        <div
                                                            className="quantity">{t("ecommerce.myreturns.comment-label")} {item.comment}</div>
                                                    </div>
                                                </div>

                                                <div className="col-sm-12 col-lg-2 return-status">
                                                    <div className="font-weight-bold">
                                                        {returnStatus}
                                                    </div>

                                                    <div>
                                                        {t("ecommerce.myreturns.quantity-label")} {item.quantity}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>)
                        })}
                    </div>
                </Box>
            ))}
        </StyledReturn>
    )
}

type IReturnFormValues = {
    email: string
    orderNumber: string
    slider: number | null
    firstName: string
    lastName: string
};

export const GuestReturnForm = () => {
    const container = document.getElementById("return-form");
    const {t} = React.useContext(LabelContext);
    const [requestProcessed, setRequestProcessed] = React.useState("NO");
    const {ajaxMiddleware$, token} = React.useContext(RESTContext)

    let requiredError = t("ecommerce.form-messagge.required")
    let inputMaxLengthError = t("ecommerce.form-messagge.error-input-max-length")
    let invalidEmail = t("ecommerce.form-messagge.email-invalid")
    let inputMaxLengthOrderNumber = 9

    const schema = yup.object().shape({
        firstName: yup.string().required(requiredError),
        lastName: yup.string().required(requiredError),
        orderNumber: yup.string().max(inputMaxLengthOrderNumber, inputMaxLengthError).required(requiredError),
        email: yup.string().email(invalidEmail).required(requiredError),
        // honeypot slider
        slider: yup
            .number()
            .required(requiredError)
    });

    const {
        register,
        handleSubmit,
        setValue,
        formState: {errors, isSubmitted},
    } = useForm<IReturnFormValues>({
        resolver: yupResolver(schema),
        defaultValues: {slider: null}
    });

    const onSubmit: SubmitHandler<IReturnFormValues> = (data) => {
        const body = {
            email: data.email,
            orderNumber: data.orderNumber,
            projectKey: PROJECT_KEY,
            authToken: token?.access_token
        }
        ajaxMiddleware$(calls.Project.requestReturn(), undefined, body).subscribe({
            next: (res: any) => {
                if (res.code === 200) {
                    setRequestProcessed("YES")
                    const params = new URLSearchParams({
                        orderNumber: data.orderNumber,
                        email: data.email,
                    });

                    if (data.firstName) params.set('firstName', data.firstName);
                    if (data.lastName) params.set('lastName', data.lastName);

                    window.location.href = `${BRX_BASE_URL}/return?${params.toString()}`;
                } else if(res.code === 404) {
                    setRequestProcessed("NOTFOUND")
                } else {
                    setRequestProcessed("ERROR")
                }
            },
            error: (err) => {
                setRequestProcessed("ERROR")
            },
        })
    };

    if (!container) return null;
    return ReactDOM.createPortal(
        <>
            <StyledReturn>
                <Box className="unboxed secondary unbordered">
                    <div className="row justify-content-md-center mx-0">
                        <div className="col-md-6">
                            <Box className="secondary unbordered returnForm">
                                <h1>{t("ecommerce.return-form.request-title")}</h1>

                                <div className="centerBlocks">
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <InputElement
                                            label={t("ecommerce.return-form.first-name-field")}
                                            id="firstName"
                                            externalRegister={register("firstName")}
                                            errors={errors.firstName?.message}
                                        />
                                        <InputElement
                                            label={t("ecommerce.return-form.last-name-field")}
                                            id="lastName"
                                            externalRegister={register("lastName")}
                                            errors={errors.lastName?.message}
                                        />
                                        <InputElement
                                            label={t("ecommerce.return-form.email-field")}
                                            id="email"
                                            externalRegister={register("email")}
                                            errors={errors.email?.message}
                                        />
                                        <InputElement
                                            label={t("ecommerce.return-form.order-number-field")}
                                            id="orderNumber"
                                            externalRegister={register("orderNumber")}
                                            errors={errors.orderNumber?.message}
                                            className="mb-0"
                                        />
                                        <span
                                            className="eforms-hint">{t("ecommerce.return-form.order-number-hint")}</span>

                                        {/* Hidden field for Yup */}
                                        <input type="hidden" {...register("slider")} />

                                        <AntiSpamSlider
                                            onValidChange={(val: any) =>
                                                setValue("slider", val, { shouldValidate: isSubmitted })
                                            }
                                            isError={!!errors.slider}
                                        />

                                        {requestProcessed === "ERROR" && (
                                            <>
                                                <div className="text-center message--alert small">
                                                    <p>
                                                        {t("ecommerce.return-form.request-error")}
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                        {requestProcessed === "NOTFOUND" && (
                                            <>
                                                <div className="text-center message--alert small">
                                                    <p>
                                                        {t("ecommerce.return-form.request-not-found")}
                                                    </p>
                                                </div>
                                            </>
                                        )}

                                        <Button
                                            className="primary mt-4 float-right"
                                            label={t("ecommerce.return-form.request-button")}
                                            type="submit"
                                        />
                                    </form>
                                </div>
                            </Box>
                        </div>
                    </div>
                </Box>
            </StyledReturn>
        </>, container
    );
};

export const AntiSpamSlider = ({onValidChange, isError}: any) => {
    const [value, setValue] = React.useState(0)
    const {t} = React.useContext(LabelContext)

    const threshold = 90       // così funziona come nel tuo screenshot
    const sliderMax = 100
    const isValid = value >= threshold

    const handleChange = (e: any) => {
        const v = Number(e.target.value)
        setValue(v)

        if (v >= threshold) {
            onValidChange(sliderMax)
        } else {
            onValidChange(null)
        }
    }

    return (
        <div className="antispam mt-4 mb-3">
            {/* SLIDER */}
            <input
                type="range"
                min={0}
                max={100}
                value={value}
                onChange={handleChange}
                className="antispam-slider"
            />


            <div className="antispam-label mt-2 text-center">
                {!isValid && !isError && (
                    <span className="text-muted">{t("ecommerce.return-form.antispam.instruction")}</span>
                )}

                {!isValid && isError && (
                    <span className="text-danger">{t("ecommerce.return-form.antispam.error")}</span>
                )}

                {isValid && (
                    <span className="text-success">{t("ecommerce.return-form.antispam.success")}</span>
                )}
            </div>
        </div>
    )
}

